import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, MessageCircle, Navigation, Phone } from 'lucide-react';
import { openWhatsApp } from '@/lib/whatsapp';
import { formatDistance } from '@/lib/geolocation';
import { generateResponsiveImageUrls } from '@/lib/imageOptimization';
import {
  type ContactStore,
  formatOpeningHours,
  getStoreStatus,
  hasOpeningHoursData,
} from './contactHours';
import { OpeningHoursWeek } from './OpeningHoursWeek';
import { ContactStoreMap } from './ContactStoreMap';

interface ContactStoreCardProps {
  store: ContactStore;
  isMultiStore: boolean;
  distance: number | null;
  buttonColor: string;
  secondaryColor: string;
  onRequestDistance: () => void;
}

export function ContactStoreCard({
  store,
  isMultiStore,
  distance,
  buttonColor,
  secondaryColor,
  onRequestDistance,
}: ContactStoreCardProps) {
  const schedules = hasOpeningHoursData(store.openingHours)
    ? formatOpeningHours(store.openingHours)
    : [];
  const status = hasOpeningHoursData(store.openingHours)
    ? getStoreStatus(store.openingHours)
    : null;
  const hasFrontage = !!store.frontage;

  const frontageImage = useMemo(
    () =>
      store.frontage
        ? generateResponsiveImageUrls(store.frontage, 640, 373)
        : null,
    [store.frontage]
  );

  const frontageBlurImage = useMemo(
    () =>
      store.frontage
        ? generateResponsiveImageUrls(store.frontage, 400, 233)
        : null,
    [store.frontage]
  );

  return (
    <article className="wl6-contact-store">
      <div className="wl6-contact-store__banner">
        {hasFrontage && frontageImage ? (
          <>
            <div className="wl6-contact-store__banner-bg" aria-hidden="true">
              <img
                src={frontageBlurImage?.src ?? frontageImage.src}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <div className="wl6-contact-store__banner-bg-dim" />
            </div>

            <div className="wl6-contact-store__banner-main">
              <img
                src={frontageImage.src}
                srcSet={frontageImage.srcSet}
                sizes="(max-width: 640px) 100vw, 640px"
                width={640}
                height={373}
                alt={`Fachada ${store.company}`}
                loading="lazy"
                decoding="async"
              />
              <div className="wl6-contact-store__banner-overlay" aria-hidden="true" />

              {isMultiStore && store.company && (
                <div className="wl6-contact-store__banner-title">
                  <h2>{store.company}</h2>
                </div>
              )}

              <div className="wl6-contact-store__actions">
                {store.whatsapp?.ios && (
                  <Button
                    type="button"
                    size="lg"
                    className="flex-1 bg-green-600/95 hover:bg-green-700 text-white font-semibold backdrop-blur-sm shadow-lg border-0"
                    onClick={() => openWhatsApp(store.whatsapp?.ios)}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                )}
                {store.phone?.ios && (
                  <Button
                    type="button"
                    size="lg"
                    className="flex-1 bg-white/95 hover:bg-white text-[var(--wl6-text)] font-semibold backdrop-blur-sm shadow-lg border-0"
                    onClick={() => window.open(store.phone?.ios, '_blank')}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Ligar
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="wl6-contact-store__banner-fallback" aria-hidden="true" />
        )}
      </div>

      <div className="wl6-contact-store__info">
        <div className="wl6-contact-store__info-head">
          <div>
            {isMultiStore ? (
              <p className="wl6-eyebrow">Visite-nos</p>
            ) : (
              <p className="wl6-eyebrow">Nossa loja</p>
            )}
            {(!isMultiStore || !hasFrontage) && (
              <h2 className="wl6-contact-store__name">{store.company}</h2>
            )}
          </div>
          {distance !== null ? (
            <span
              className="wl6-contact-store__distance"
              style={{ backgroundColor: secondaryColor }}
            >
              <Navigation className="h-3.5 w-3.5" />
              {formatDistance(distance)}
            </span>
          ) : (
            <button
              type="button"
              onClick={onRequestDistance}
              className="wl6-contact-store__distance-btn"
              style={{ color: buttonColor }}
            >
              Ver distância
            </button>
          )}
        </div>

        {store.place?.address && (
          <p className="wl6-contact-store__address">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            {store.place.address}
          </p>
        )}

        <ContactStoreMap store={store} />

        {schedules.length > 0 && status && (
          <OpeningHoursWeek schedules={schedules} status={status} />
        )}
      </div>
    </article>
  );
}
